// Shared visual primitives for the Offload UI (CardIngestWindow and all its
// sub-components under this folder) — kept in one place so every component
// draws from the same palette/spacing instead of redefining it locally.
// House style: strict black/white base, color reserved for status
// badges/indicators only, Inter for UI text (mirrors CompanionWindow.jsx's
// local theme constants, same accent/ok/warn/err semantics).
export const FONT = '"Inter", -apple-system, BlinkMacSystemFont, sans-serif';

export const LIGHT = {
  bg: '#ffffff', subtleBg: '#f7f7f6', chip: '#f2f2f0', text: '#141413', muted: '#8a8a86',
  rule: '#e8e7e3', accent: '#141413', onAccent: '#ffffff', ok: '#1a9c4a', warn: '#c9860a', err: '#d1453b',
  inputBg: '#ffffff',
};
export const DARK = {
  bg: '#141413', subtleBg: '#1c1c1b', chip: '#232322', text: '#f2f2f0', muted: '#8a8a86',
  rule: '#2a2a28', accent: '#f2f2f0', onAccent: '#141413', ok: '#51cf66', warn: '#ffc107', err: '#f87171',
  inputBg: '#1c1c1b',
};

export function btnStyle(c, variant = 'default') {
  const base = {
    padding: '6px 14px', borderRadius: '6px', fontFamily: FONT, fontSize: '12px',
    fontWeight: 500, cursor: 'pointer', border: `1px solid ${c.rule}`, background: 'transparent',
    color: c.text, display: 'inline-flex', alignItems: 'center', gap: '6px',
  };
  if (variant === 'primary') return { ...base, background: c.accent, borderColor: c.accent, color: c.onAccent };
  if (variant === 'danger') return { ...base, background: 'transparent', borderColor: c.err, color: c.err };
  if (variant === 'ghost') return { ...base, border: 'none' };
  return base;
}

export function formatBytes(n) {
  if (n === null || n === undefined) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Elapsed/ETA formatting for the overall-job progress summary — e.g. "2m 14s".
export function formatDuration(ms) {
  if (ms === null || ms === undefined || !isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export const INGEST_STATUS_META = {
  queued: { label: 'Queued', color: 'muted' },
  running: { label: 'Running', color: 'accent' },
  completed: { label: 'Completed', color: 'ok' },
  completed_with_errors: { label: 'Completed (errors)', color: 'warn' },
  failed: { label: 'Failed', color: 'err' },
  cancelled: { label: 'Cancelled', color: 'muted' },
  interrupted: { label: 'Interrupted', color: 'err' },
};

export const PROXY_STATUS_META = {
  queued: { label: 'Queued', iconName: 'Clock' },
  running: { label: 'Encoding…', iconName: 'Loader2', spin: true },
  completed: { label: 'Completed', iconName: 'CheckCircle2' },
  failed: { label: 'Failed', iconName: 'XCircle' },
  cancelled: { label: 'Cancelled', iconName: 'XCircle' },
};

export const PROTOCOL_BADGE_COLOR = { ftp: 'muted', ftps: 'ok', sftp: 'ok' };

export const CARD_INGEST_SPIN_KEYFRAMES = '@keyframes cardIngestSpin { to { transform: rotate(360deg); } }';
